import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { CalendarDays, Headphones, ArrowRight, Share2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { useAudiobook } from "@/context/AudiobookContext";
import { shareDayCard } from "@/lib/hajjShareCard";
import { REMINDER_UI as UI } from "@/data/manasikReminders";
import { AUDIOBOOK_UI } from "@/data/audiobookI18n";
import { hajjChapterIndex } from "@/lib/audiobook";
import { hajjContext, formatHijri, timelineDayFor, readCorrection } from "@/lib/manasikReminders";
import { fill } from "@/lib/utils";

const COUNTDOWN_WINDOW_DAYS = 30;

// Dashboard card: shows during 8–13 Dzulhijjah (today's manasik) or the 30-day run-up to Tarwiyah.
export function HajjTodayCard() {
  const { tc } = useLang();
  const { user } = useAuth();
  const audiobook = useAudiobook();
  const [sharing, setSharing] = useState(false);
  const ctx = useMemo(() => hajjContext(readCorrection()), []);
  const show = ctx.phase === "hajj" || (ctx.daysToTarwiyah != null && ctx.daysToTarwiyah > 0 && ctx.daysToTarwiyah <= COUNTDOWN_WINDOW_DAYS);
  if (!show) return null;
  const tlDay = ctx.dayInfo ? timelineDayFor(ctx.hijri.day) : null;
  const share = async () => {
    setSharing(true);
    try {
      await shareDayCard({ tlDay, dateLine: fill(tc(UI.todayIs), { date: formatHijri(ctx.hijri, tc) }), tc, name: user?.name });
      toast.success(tc(UI.shareReady));
    } catch { toast.error(tc(UI.shareFail)); }
    finally { setSharing(false); }
  };

  return (
    <Card className="rounded-3xl border-[hsl(var(--gold))]/40 bg-[hsl(var(--gold))]/5 shadow-sm p-5 flex flex-col gap-3 sm:flex-row sm:items-center" data-testid="hajj-today-card">
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[hsl(var(--gold))]/20 text-[hsl(var(--gold))]"><CalendarDays className="h-6 w-6" /></span>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--gold))]" data-testid="hajj-today-date">{fill(tc(UI.todayIs), { date: formatHijri(ctx.hijri, tc) })}</p>
        {ctx.dayInfo ? (
          <>
            <p className="font-medium" data-testid="hajj-today-title">{tc(ctx.dayInfo.title)}</p>
            <p className="text-xs text-muted-foreground">{tc(ctx.dayInfo.body)}</p>
          </>
        ) : (
          <p className="font-medium" data-testid="hajj-today-countdown">{fill(tc(UI.countdown), { n: ctx.daysToTarwiyah })}</p>
        )}
      </div>
      <div className="flex shrink-0 flex-wrap gap-2">
        {tlDay && (
          <>
            <button onClick={() => audiobook.jump(hajjChapterIndex(tlDay))} data-testid="hajj-today-listen" className="inline-flex h-10 items-center gap-1.5 rounded-full bg-primary px-4 text-sm font-medium text-white hover:opacity-90">
              <Headphones className="h-4 w-4" /> {tc(AUDIOBOOK_UI.listen)}
            </button>
            <button onClick={share} disabled={sharing} data-testid="hajj-today-share" aria-label={tc(UI.share)} title={tc(UI.share)} className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[hsl(var(--gold))]/60 bg-[hsl(var(--gold))]/10 text-[hsl(var(--gold))] hover:bg-[hsl(var(--gold))]/20 disabled:opacity-60">
              {sharing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />}
            </button>
          </>
        )}
        <Link to="/haji-timeline" data-testid="hajj-today-open" className="inline-flex h-10 items-center gap-1.5 rounded-full border border-border px-4 text-sm font-medium hover:bg-secondary">
          {tc(UI.openTimeline)} <ArrowRight className="h-4 w-4 rtl:-scale-x-100" />
        </Link>
      </div>
    </Card>
  );
}
